import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { Link, useParams } from 'react-router'
import { api, type SpaceDetail } from '../../lib/api'
import { AddSheet } from '../items/AddSheet'
import { TabBar } from '../../components/TabBar'
import { BoutonActualiser } from '../../components/BoutonActualiser'
import { IconeOutil } from '../spaces/IconeOutil'
import { layout, sousLaBarre } from '../../design/tokens'

/** Centimes vers euros, sans flottant ni dépendance à une culture. */
function euros(centimes: number) {
  return `${Math.floor(centimes / 100)},${String(centimes % 100).padStart(2, '0')} €`
}

/**
 * Les outils d'un espace, sur leur propre écran — décision L50.
 *
 * Ils partageaient l'écran de l'espace avec les catégories et leur gestion, qui s'y empilaient.
 * Séparés, chacun respire : les catégories sont le contenu, les outils sont les instruments, et
 * la barre basse mène directement à l'un ou à l'autre.
 *
 * Chaque tuile dit ce qu'elle a à annoncer, et prend la couleur d'accent seulement dans ce cas.
 */
export function ToolsScreen() {
  const { spaceId = '' } = useParams()
  const [feuille, setFeuille] = useState(false)

  const { data } = useQuery<SpaceDetail>({
    queryKey: ['espace', spaceId],
    queryFn: () => api.space(spaceId),
  })

  const outils = data
    ? [
        {
          to: `/espace/${spaceId}/courses`,
          libelle: 'Courses',
          icone: 'panier' as const,
          detail:
            data.tools.shoppingRemaining > 0
              ? `${data.tools.shoppingRemaining} à prendre`
              : 'Liste à jour',
          actif: data.tools.shoppingRemaining > 0,
        },
        {
          to: `/espace/${spaceId}/agenda`,
          libelle: 'Agenda',
          icone: 'calendrier' as const,
          detail:
            data.tools.agendaToday > 0 ? `${data.tools.agendaToday} aujourd'hui` : 'Rien de prévu',
          actif: data.tools.agendaToday > 0,
        },
        {
          to: `/espace/${spaceId}/depenses`,
          libelle: 'Dépenses',
          icone: 'porte-monnaie' as const,
          detail:
            data.tools.balanceCents > 0 ? `${euros(data.tools.balanceCents)} en jeu` : 'Tout est à jour',
          actif: data.tools.balanceCents > 0,
        },
        {
          to: `/espace/${spaceId}/galerie`,
          libelle: 'Galerie',
          icone: 'image' as const,
          detail:
            data.tools.photos > 0
              ? `${data.tools.photos} photo${data.tools.photos > 1 ? 's' : ''}`
              : 'Aucune photo',
          actif: data.tools.photos > 0,
        },
        {
          to: `/espace/${spaceId}/cadeaux`,
          libelle: 'Cadeaux',
          icone: 'paquet' as const,
          detail:
            data.tools.giftsPending > 0 ? `${data.tools.giftsPending} en cours` : 'Rien en cours',
          actif: data.tools.giftsPending > 0,
        },
      ]
    : []

  return (
    <main
      className="mx-auto min-h-dvh bg-surface"
      style={{
        maxWidth: layout.column,
        paddingInline: layout.gutter,
        paddingBottom: sousLaBarre(32),
      }}
    >
      <header className="flex items-center justify-between gap-3" style={{ height: layout.header }}>
        <h1 className="grow truncate font-serif text-screen">Outils</h1>
        <BoutonActualiser />
      </header>

      {data ? (
        <p className="text-meta text-ink-muted" style={{ paddingBottom: 12 }}>
          {data.space.name}
        </p>
      ) : null}

      <ul className="grid grid-cols-2" style={{ gap: layout.gridGap }}>
        {outils.map((outil) => (
          <li key={outil.to}>
            <Link
              to={outil.to}
              className="flex h-full items-center gap-3 rounded-card border border-border bg-card px-4"
              style={{ minHeight: 80 }}
            >
              <IconeOutil nom={outil.icone} actif={outil.actif} />
              <span className="grow truncate">
                <span
                  className="block truncate text-card-title font-semibold"
                  style={{ color: outil.actif ? 'var(--color-accent)' : 'var(--color-ink)' }}
                >
                  {outil.libelle}
                </span>
                <span className="block truncate text-meta text-ink-muted">{outil.detail}</span>
              </span>
            </Link>
          </li>
        ))}
      </ul>

      {/* Cet écran ne contient rien qu'on ajoute : le « + » retombe alors sur le geste par
          défaut de l'application, ajouter une fiche. Un bouton qui ne ferait rien serait pire. */}
      <TabBar onAdd={() => setFeuille(true)} activeSpaceId={spaceId} />

      {feuille ? <AddSheet spaceId={spaceId} onClose={() => setFeuille(false)} /> : null}
    </main>
  )
}
